import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, Dialog, EmptyState, Field, PageHeader, Skeleton, Spinner, Table, Td, Th, useConfirm, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import type { Group } from './VehiclesPage';

const PALETTE = ['#14532D', '#2563EB', '#7C3AED', '#DB2777', '#EA580C', '#0891B2', '#65A30D', '#475569'];

export function GroupsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['vehicle-groups'], queryFn: () => api<Group[]>('/vehicle-groups') });
  const [editing, setEditing] = useState<Group | 'new' | null>(null);
  const remove = useMutation({ mutationFn: (id: string) => api(`/vehicle-groups/${id}`, { method: 'DELETE' }), onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['vehicle-groups'] }); }, onError: toast.error });

  return (
    <div className="flex flex-col gap-4">
      <Link to="/vehicles" className="flex items-center gap-1 text-sm text-text-muted hover:text-text"><ArrowRight size={14} className="ltr:rotate-180" /> {t('nav.vehicles')}</Link>
      <PageHeader title={t('vehicles.groups')} subtitle={t('vehicles.groupsHint')} actions={<Can perm="groups:create"><button className="btn-primary" onClick={() => setEditing('new')}><Plus size={16} /> {t('vehicles.addGroup')}</button></Can>} />
      {q.isLoading ? <Skeleton className="h-48" /> : !q.data?.length ? <div className="card"><EmptyState title={t('common.empty')} /></div> : (
        <Table>
          <thead><tr><Th>{t('vehicles.group')}</Th><Th>{t('nav.vehicles')}</Th><Th className="w-1" sticky="end" /></tr></thead>
          <tbody>
            {q.data.map((g) => (
              <tr key={g.id} className="group hover:bg-surface-hover">
                <Td><span className="inline-flex items-center gap-2 font-semibold"><span className="size-3 rounded-full" style={{ background: g.color ?? '#94A3B8' }} />{g.name}</span></Td>
                <Td><Badge tone="neutral">{g._count?.vehicles ?? 0}</Badge></Td>
                <Td sticky="end"><div className="flex justify-end gap-1"><Can perm="groups:update"><button className="btn-ghost size-9 !p-0" aria-label={t('common.edit')} onClick={() => setEditing(g)}><Pencil size={16} /></button></Can><Can perm="groups:delete"><button className="btn-ghost size-9 !p-0 text-danger-fg" aria-label={t('common.delete')} onClick={() => confirmDialog(t('vehicles.deleteGroupConfirm'), { danger: true }).then((ok) => ok && remove.mutate(g.id))}><Trash2 size={16} /></button></Can></div></Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {editing ? <GroupDialog group={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

function GroupDialog({ group, onClose }: { group: Group | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(group?.name ?? '');
  const [color, setColor] = useState(group?.color ?? PALETTE[0]!);
  const save = useMutation({
    mutationFn: () => (group ? api(`/vehicle-groups/${group.id}`, { method: 'PATCH', json: { name, color } }) : api('/vehicle-groups', { method: 'POST', json: { name, color } })),
    onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['vehicle-groups'] }); onClose(); },
    onError: toast.error,
  });
  return (
    <Dialog open onClose={onClose} title={group ? t('common.edit') : t('vehicles.addGroup')} footer={<><button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button><button className="btn-primary" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button></>}>
      <div className="grid gap-4">
        <Field label={t('vehicles.groupName')} required>{(id) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} />}</Field>
        <div><span className="label">{t('vehicles.color')}</span><div className="flex flex-wrap gap-2">{PALETTE.map((c) => <button key={c} type="button" aria-label={c} className={`size-8 rounded-full border-2 ${color === c ? 'border-text' : 'border-transparent'}`} style={{ background: c }} onClick={() => setColor(c)} />)}</div></div>
      </div>
    </Dialog>
  );
}
